
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "checkbox",
    "shipmentNo",
    "emailField",
    "emailGroup",
    "shippingTypeSelect",
    "shippingTypeGroup",
    "importerOfRecordSelect",
    "importerOfRecordGroup",
    "countryOfOriginSelect",
    "countryOfOriginGroup",
    "incotermsSelect",
    "incotermsGroup"
  ]

  connect() {
    this.adoptRenderedValuesAsDefaults()
    this.toggleEmailRequirement()
    this.toggleShippingType()
    this.setupShippingListeners()
  }

  disconnect() {
    if (this._shippingCostField && this._onShippingCostInput) {
      this._shippingCostField.removeEventListener('input', this._onShippingCostInput)
    }
    if (this._freeShippingField && this._onFreeShippingChange) {
      this._freeShippingField.removeEventListener('change', this._onFreeShippingChange)
    }
  }

  adoptRenderedValuesAsDefaults() {
    const selects = [
      this.hasImporterOfRecordSelectTarget && this.importerOfRecordSelectTarget,
      this.hasIncotermsSelectTarget && this.incotermsSelectTarget
    ]
    selects.forEach(select => {
      if (select && select.value) {
        select.dataset.defaultValue = select.value
      }
    })
  }

  setupShippingListeners() {
    const form = this.element.closest('form')
    if (!form) return

    const shippingCostField = form.querySelector('[name="proposal[shipping_attributes][cost]"]')
    if (!shippingCostField) return

    const freeShippingField = form.querySelector('[name="proposal[shipping_attributes][free_shipping]"][type="checkbox"]')

    this._shippingCostField = shippingCostField
    this._freeShippingField = freeShippingField
    this._onShippingCostInput = () => this.handleShippingChange(shippingCostField, freeShippingField)
    this._onFreeShippingChange = () => this.handleShippingChange(shippingCostField, freeShippingField)

    shippingCostField.addEventListener('input', this._onShippingCostInput)
    if (freeShippingField) {
      freeShippingField.addEventListener('change', this._onFreeShippingChange)
    }
    shippingCostField.dispatchEvent(new Event('input'))
  }

  handleShippingChange(shippingCostField, freeShippingField) {
    const cost = parseFloat(shippingCostField.value)
    const isFreeShipping = freeShippingField && freeShippingField.checked

    if (cost > 0 && !isFreeShipping) {
      this.checkboxTarget.checked = true
    }
    if (this.hasShipmentNoTarget) {
      this.shipmentNoTarget.disabled = cost > 0 && !isFreeShipping
    }
    this.toggleEmailRequirement()
  }

  toggleEmailRequirement() {
    const isChecked = this.checkboxTarget.checked

    // Toggle email and shipping type fields
    this.setFieldState(this.emailFieldTarget, this.emailGroupTarget, isChecked)
    this.setFieldState(this.shippingTypeSelectTarget, this.shippingTypeGroupTarget, isChecked)

    // Clear importer of record when unchecked (only exists when consolidated_billing is enabled)
    if (!isChecked && this.hasImporterOfRecordSelectTarget) {
      this.clearImporterOfRecord()
    }

    if (!isChecked && this.hasCountryOfOriginSelectTarget) {
      this.clearCountryOfOrigin()
    }

    if (!isChecked && this.hasIncotermsSelectTarget) {
      this.clearIncoterms()
    }
  }

  toggleShippingType() {
    const isInternational = this.shippingTypeSelectTarget.value === 'international'

    if (this.hasImporterOfRecordSelectTarget && this.hasImporterOfRecordGroupTarget) {
      if (isInternational) {
        this.importerOfRecordGroupTarget.classList.remove('d-none')
        this.importerOfRecordSelectTarget.required = true
        this.importerOfRecordSelectTarget.disabled = false
        if (!this.importerOfRecordSelectTarget.value) {
          this.importerOfRecordSelectTarget.value = this.importerOfRecordSelectTarget.dataset.defaultValue || ''
        }
      } else {
        this.clearImporterOfRecord()
      }
    }

    if (this.hasCountryOfOriginSelectTarget && this.hasCountryOfOriginGroupTarget) {
      if (isInternational) {
        this.countryOfOriginGroupTarget.classList.remove('d-none')
        this.countryOfOriginSelectTarget.required = true
        this.countryOfOriginSelectTarget.disabled = false
      } else {
        this.clearCountryOfOrigin()
      }
    }

    if (this.hasIncotermsSelectTarget && this.hasIncotermsGroupTarget) {
      if (isInternational) {
        this.incotermsGroupTarget.classList.remove('d-none')
        this.incotermsSelectTarget.required = true
        this.incotermsSelectTarget.disabled = false
        if (!this.incotermsSelectTarget.value) {
          this.incotermsSelectTarget.value = this.incotermsSelectTarget.dataset.defaultValue || ''
        }
      } else {
        this.clearIncoterms()
      }
    }
  }

  // Helper method to set field visibility, required state, and value
  setFieldState(field, group, isActive) {
    if (isActive) {
      field.required = true
      group.classList.remove('d-none')
    } else {
      field.value = ''
      field.required = false
      group.classList.add('d-none')
    }
  }

  // Helper method to clear and hide importer of record field
  clearImporterOfRecord() {
    this.importerOfRecordSelectTarget.value = ''
    this.importerOfRecordSelectTarget.required = false
    this.importerOfRecordSelectTarget.disabled = true
    this.importerOfRecordGroupTarget.classList.add('d-none')
  }

  clearCountryOfOrigin() {
    this.countryOfOriginSelectTarget.value = ''
    this.countryOfOriginSelectTarget.required = false
    this.countryOfOriginSelectTarget.disabled = true
    this.countryOfOriginGroupTarget.classList.add('d-none')
  }

  clearIncoterms() {
    this.incotermsSelectTarget.value = ''
    this.incotermsSelectTarget.required = false
    this.incotermsSelectTarget.disabled = true
    this.incotermsGroupTarget.classList.add('d-none')
  }
}
